import { Box, Typography, Container } from '@mui/material';
import { Link } from 'react-router-dom';

function PrivacyPage() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ flex: 1, py: 4 }}>
        <Link to="/" style={{ color: '#1976d2', fontSize: 14, textDecoration: 'none' }}>
          &larr; Back
        </Link>

        <Typography variant="h4" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
          Privacy Policy
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 4 }}>
          Last updated: April 12, 2026
        </Typography>

        <Section title="1. Information We Collect">
          When you create a Third Place account, we collect your email address, username, and optional
          display name. When you use the app, we collect your location (only while the app is in use)
          to show nearby events and let friends know where you are. If you choose to use live location
          sharing, your precise GPS coordinates are broadcast to conversation participants in real time
          (approximately every 10 seconds) for up to one hour per session. We also collect photos you
          choose to upload for your profile, events, or chat messages, messages you send in
          conversations, poll votes and responses, event RSVP responses (accepted, tentative, or
          declined), message read receipts, and device identifiers for push notifications. If you
          choose to save an event to your device calendar, we access your calendar with write-only
          permission — we do not read any existing calendar data.
        </Section>

        <Section title="2. How We Use Your Information">
          We use your information to provide the core features of Third Place: showing you nearby
          events, delivering messages between you and your friends, sending push notifications for new
          events, messages, and friend requests, and displaying your profile to other users. We do not
          sell your personal information to third parties.
        </Section>

        <Section title="3. Data Sharing">
          Your username and optional display name are visible to other Third Place users. Your display
          name is only shown to accepted friends. Your location is used to display events near you. If
          you opt in to live location sharing, your real-time coordinates are shared with participants
          in that conversation for the duration of the session. We use Supabase for data storage and
          authentication, OneSignal for push notification delivery, and Apple Push Notification Service
          (APNs) to deliver notifications to your device. These services may process your data as
          described in their respective privacy policies.
        </Section>

        <Section title="4. Data Storage &amp; Security">
          Your data is stored securely using Supabase (hosted PostgreSQL with row-level security). All
          communication between the app and our servers uses HTTPS encryption. Authentication tokens
          are stored securely on your device by the Supabase SDK.
        </Section>

        <Section title="5. Your Rights">
          You can delete your account at any time from Settings. This permanently removes all your data
          including your profile, messages, events, friendships, and photos. You can update your
          profile information, display name, and avatar at any time. You can block other users to
          prevent them from contacting you.
        </Section>

        <Section title="6. Data Retention">
          Messages, conversation data, and friend connections are stored indefinitely while your
          account is active. Events remain in our database after they expire but are no longer
          displayed in the app. Live location sharing data is transient and expires automatically after
          the session ends. When you delete your account, all of your data — including your profile,
          messages, events, friendships, and photos — is permanently removed.
        </Section>

        <Section title="7. Children's Privacy">
          Third Place is not intended for children under 13. We do not knowingly collect personal
          information from children under 13. If you believe a child under 13 has provided us with
          personal information, please contact us.
        </Section>

        <Section title="8. Changes to This Policy">
          We may update this privacy policy from time to time. We will notify you of significant
          changes through the app. Your continued use of Third Place after changes constitutes
          acceptance of the updated policy.
        </Section>

        <Section title="9. Contact">
          If you have questions about this privacy policy or your data, please contact us at{' '}
          <a href="mailto:privacy@thirdplaceapp.com">privacy@thirdplaceapp.com</a>.
        </Section>
      </Container>
    </Box>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
}

export default PrivacyPage;
